import { Link } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useTranslation, Trans } from 'react-i18next';
import { EmptyState } from '@/components/ui/empty-state';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TabBar } from '@/components/ui/tab-bar';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import {
  ShoppingCart, Package, DollarSign, Users,
  TrendingUp, ArrowRight, Boxes,
  Calculator, Receipt, Wallet, MapPin,
  ArrowRightLeft, BadgePercent, AlertTriangle, CreditCard,
  CheckCircle2, Star, X, Printer, Banknote, Building2, Lock,
  ShoppingBag, Landmark, PieChart,
  UtensilsCrossed, CalendarDays, LayoutGrid,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import api from '@/lib/api';
import { addonLabel, planLabel } from '@/lib/catalog-labels';
import { formatCurrency, formatRelativeDate } from '@/lib/utils';
import { DataCard } from '@/components/ui/data-card';
import { StatCard } from '@/components/ui/stat-card';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { PoweredBy } from '@/components/print/PoweredBy';
import { useChartColors } from '@/lib/chart-colors';

// POS-I190: every money value on the dashboard cards and lists shows exactly two decimals
// ("Rs 2,135.20", "Rs 912,920.00"). Local on purpose: the global formatCurrency (0 to 2 decimals)
// is used on 100+ other screens and is not changed here.
const MONEY_2DP = new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatMoney2(amount: number | null | undefined): string {
  const n = Number(amount);
  return MONEY_2DP.format(Number.isFinite(n) ? n : 0);
}

function UsageGauge({
  used,
  max,
  label,
  trialPlanHref,
}: {
  used: number;
  max: number | null;
  label: string;
  /** POS-I193: set for a trial gauge whose limit is information, not a fault; the link opens My Plan */
  trialPlanHref?: string | null;
}) {
  const { t } = useTranslation('common');
  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : null;
  // In a trial the plan limit is shown calmly (amber, with the next step) instead of a red alarm on day one.
  const trialAtLimit = trialPlanHref !== undefined && max !== null && max > 0 && used >= max;
  const color = pct === null
    ? 'bg-info'
    : trialAtLimit ? 'bg-warning' : pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-warning' : 'bg-success';
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          <bdi dir="ltr">{used.toLocaleString()}{max !== null ? ` / ${max.toLocaleString()}` : ' (∞)'}</bdi>
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: pct !== null ? `${pct}%` : '100%', opacity: pct === null ? 0.3 : 1 }} />
      </div>
      {trialAtLimit ? (
        <p className="text-xs text-warning-text flex flex-wrap items-center gap-x-1">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span>{t('dashboardUsageTrialAtLimit', { count: max })}</span>
          {trialPlanHref && (
            <Link to={trialPlanHref} className="font-semibold underline underline-offset-2 hover:opacity-80">
              {t('dashboardUsageTrialLink')}
            </Link>
          )}
        </p>
      ) : pct !== null && pct >= 80 && (
        <p className="text-xs text-warning-text flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {t('dashboardUsagePctOfLimit', { pct })}
        </p>
      )}
    </div>
  );
}

const WHATSAPP_URL = (msg: string) =>
  `https://wa.me/923330950000?text=${encodeURIComponent(msg)}`;

// label/desc are i18n keys (common ns) resolved at render.
const ALL_FEATURES = [
  { label: 'dashboardFeaturePosLabel', desc: 'dashboardFeaturePosDesc', perm: 'pos:*', price: null },
  { label: 'dashboardFeatureCatalogueLabel', desc: 'dashboardFeatureCatalogueDesc', perm: 'products:*', price: null },
  { label: 'dashboardFeatureCustomersLabel', desc: 'dashboardFeatureCustomersDesc', perm: 'customers:*', price: null },
  { label: 'dashboardFeatureReportsLabel', desc: 'dashboardFeatureReportsDesc', perm: 'reports:*', price: null },
  { label: 'dashboardFeatureInventoryLabel', desc: 'dashboardFeatureInventoryDesc', perm: 'inventory:*', price: 800 },
  { label: 'dashboardFeatureFbrLabel', desc: 'dashboardFeatureFbrDesc', perm: 'fbr:*', price: 1000 },
  { label: 'dashboardFeatureExpensesLabel', desc: 'dashboardFeatureExpensesDesc', perm: 'expenses:*', price: 500 },
  { label: 'dashboardFeatureAccountingLabel', desc: 'dashboardFeatureAccountingDesc', perm: 'accounting:*', price: 1500 },
  { label: 'dashboardFeatureBankLabel', desc: 'dashboardFeatureBankDesc', perm: 'bank:*', price: 1000 },
  { label: 'dashboardFeatureTaxLabel', desc: 'dashboardFeatureTaxDesc', perm: 'tax-rates:*', price: 1000 },
  { label: 'dashboardFeatureLocationsLabel', desc: 'dashboardFeatureLocationsDesc', perm: 'locations:*', price: 1000 },
  { label: 'dashboardFeatureTransfersLabel', desc: 'dashboardFeatureTransfersDesc', perm: 'transfers:*', price: 1000 },
  { label: 'dashboardFeaturePromotionsLabel', desc: 'dashboardFeaturePromotionsDesc', perm: 'promotions:*', price: 1000 },
  { label: 'dashboardFeatureApiLabel', desc: 'dashboardFeatureApiDesc', perm: 'api:*', price: 1500 },
];

const ONBOARDING_STEPS = [
  { icon: '🏪', title: 'dashboardOnboardProductTitle', desc: 'dashboardOnboardProductDesc', href: '/products', cta: 'dashboardOnboardProductCta' },
  { icon: '👥', title: 'dashboardOnboardCustomerTitle', desc: 'dashboardOnboardCustomerDesc', href: '/customers', cta: 'dashboardOnboardCustomerCta' },
  { icon: '💰', title: 'dashboardOnboardSaleTitle', desc: 'dashboardOnboardSaleDesc', href: '/pos', cta: 'dashboardOnboardSaleCta' },
  { icon: '🧾', title: 'dashboardOnboardFbrTitle', desc: 'dashboardOnboardFbrDesc', href: '/fbr', cta: 'dashboardOnboardFbrCta' },
  { icon: '📊', title: 'dashboardOnboardReportsTitle', desc: 'dashboardOnboardReportsDesc', href: '/reports', cta: 'dashboardOnboardReportsCta' },
];

interface FloorTable {
  status: string;
}

interface FloorSection {
  tables: FloorTable[];
}

interface DashboardTrendPoint {
  date: string;
  day: string;
  revenue: number;
  sales: number;
}

interface DashboardRecentSale {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  createdAt: string;
  customerName: string | null;
  itemCount: number;
  status?: string;
}

interface DashboardAddon {
  key?: string;
  name: string;
  /** billing:read only: price of one unit (kept for older servers) */
  price?: number;
  /** billing:read only: price of one unit */
  unitPrice?: number;
  /** billing:read only: what the line costs per month (QUANTITY: unit x quantity, others once) */
  lineTotal?: number;
  type?: string;
  quantity: number;
}

interface DashboardSubscription {
  planName: string;
  status: string;
  /** POS-I232: every module and unlimited records (live or ended trial never converted) */
  trialAccess?: boolean;
  /** billing:read only (T-468) */
  priceMonthly?: number;
  maxUsers: number | null;
  maxProducts: number | null;
  features?: unknown;
  addons?: DashboardAddon[];
  /** billing:read only (T-468) */
  estimatedBill?: number;
}

interface DashboardUsageLimit {
  used: number;
  max: number | null;
}

interface DashboardUsage {
  users: DashboardUsageLimit;
  products: DashboardUsageLimit;
  locations: DashboardUsageLimit;
  promotions: DashboardUsageLimit;
  customers: number;
  lowStock: number;
}

interface DashboardTopProduct {
  productId: string;
  productName: string;
  revenue: number;
}

interface DashboardExpenseCategory {
  name: string | null;
  total: number;
}

interface DashboardInsights {
  inventoryValue: number | null;
  expenseSummary: { total: number; categoryCount: number; categories: DashboardExpenseCategory[] } | null;
  openPurchaseOrders: { count: number; totalValue: number } | null;
  fbrToday: { submitted: number; accepted: number; rejected: number; failed: number; pending: number; offline: number } | null;
  bankBalance: number | null;
  topProducts: DashboardTopProduct[] | null;
  grossMargin: number | null;
}

/** Shape of `GET /tenant/dashboard` (tenant.service.ts getDashboard). */
interface DashboardData {
  isTest: boolean;
  subscription: DashboardSubscription | null;
  usage: DashboardUsage;
  sales: {
    last30Days: number;
    /** reports:read only (T-468) */
    last30DaysAmount: number | null;
    avgOrderValue: number | null;
    trend: DashboardTrendPoint[];
    recent: DashboardRecentSale[];
  };
  insights: DashboardInsights;
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-success/10 text-success-text',
  TRIAL: 'bg-info/10 text-info-text',
  PAST_DUE: 'bg-destructive/10 text-destructive-text',
  SUSPENDED: 'bg-muted text-muted-foreground',
};

export function DashboardPage() {
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const { hasPermission } = useAuthStore();
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const queryClient = useQueryClient();
  const { series, grid, axis } = useChartColors();

  const [zReportOpen, setZReportOpen] = useState(false);
  // In-page view: split the long dashboard into scannable tabs (T-202)
  const [tab, setTab] = useState<'overview' | 'insights' | 'plan'>('overview');
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    localStorage.getItem('onboarding-dismissed') === '1'
  );
  const [onboardingStep, setOnboardingStep] = useState(0);

  // Real-time: refresh dashboard on business events
  const handleDashboardInvalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
  }, [queryClient]);
  const handleRestaurantInvalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['restaurant-dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
  }, [queryClient]);
  useSocketEvent('sale.created', handleDashboardInvalidate);
  useSocketEvent('sale.voided', handleDashboardInvalidate);
  useSocketEvent('stock.updated', handleDashboardInvalidate);
  useSocketEvent('purchase.received', handleDashboardInvalidate);
  useSocketEvent('dining_order.created', handleRestaurantInvalidate);
  useSocketEvent('dining_order.billed', handleRestaurantInvalidate);
  useSocketEvent('dining_order.voided', handleRestaurantInvalidate);

  const canViewInventory = hasPermission('inventory:read') || hasPermission('inventory:*');
  const canViewAccounting = hasPermission('accounting:read') || hasPermission('accounting:*');
  const canViewFbr = hasPermission('fbr:read') || hasPermission('fbr:*');
  const canViewLocations = hasPermission('locations:read') || hasPermission('locations:*');
  const canViewTransfers = hasPermission('transfers:read') || hasPermission('transfers:*');
  const canViewExpenses = hasPermission('expenses:read') || hasPermission('expenses:*');
  // Strict reports:read — matches the BE gate on revenue/trend/margin (T-468); the looser
  // canViewReports below also admits accounting:* for the Reports shortcut.
  const canViewRevenue = hasPermission('reports:read') || hasPermission('reports:*');
  const canViewBilling = hasPermission('billing:read') || hasPermission('billing:*');
  const canViewReports = hasPermission('reports:read') || hasPermission('accounting:read') || hasPermission('accounting:*');
  const canViewPromotions = hasPermission('promotions:read') || hasPermission('promotions:*');
  const canViewCustomers = hasPermission('customers:read') || hasPermission('customers:*');
  const canViewPurchases = hasPermission('purchases:read') || hasPermission('purchases:*');
  const canViewBank = hasPermission('bank:read') || hasPermission('bank:*');
  const canViewRestaurant = hasPermission('restaurant:read') || hasPermission('restaurant:*');

  const { data: dashboard, isLoading } = useQuery<DashboardData>({
    queryKey: ['tenant-dashboard', selectedLocationId],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (selectedLocationId) params.locationId = selectedLocationId;
      return api.get('/tenant/dashboard', { params }).then((r) => r.data);
    },
    refetchInterval: 300_000, // 5 min safety-net; event-driven via WebSocket
  });

  const { data: fbrStats } = useQuery({
    queryKey: ['fbr-stats-dashboard'],
    queryFn: () => api.get('/fbr/submissions/stats').then((r) => r.data),
    enabled: canViewFbr,
    staleTime: 60_000,
    refetchInterval: 300_000,
  });

  const { data: restaurantStats } = useQuery({
    queryKey: ['restaurant-dashboard-stats'],
    queryFn: async () => {
      const [ordersRes, floorRes, reservationsRes] = await Promise.all([
        api.get('/dining-orders?status=OPEN,PREPARING,READY,SERVED').catch(() => ({ data: { data: [] } })),
        api.get('/restaurant/floor-plan').catch(() => ({ data: { sections: [], unassigned: [] } })),
        api.get(`/reservations?date=${new Date().toISOString().split('T')[0]}`).catch(() => ({ data: { data: [] } })),
      ]);
      const orders = ordersRes.data?.data ?? ordersRes.data ?? [];
      const floor = floorRes.data;
      const allTables = [...(floor.sections?.flatMap((s: FloorSection) => s.tables) ?? []), ...(floor.unassigned ?? [])];
      const reservations = reservationsRes.data?.data ?? reservationsRes.data ?? [];
      return {
        activeOrders: Array.isArray(orders) ? orders.length : 0,
        tablesAvailable: allTables.filter((t: FloorTable) => t.status === 'AVAILABLE').length,
        tablesTotal: allTables.length,
        todayReservations: Array.isArray(reservations) ? reservations.length : 0,
      };
    },
    enabled: canViewRestaurant,
    refetchInterval: 300_000, // 5 min safety-net; event-driven via WebSocket
  });

  const today = new Date().toISOString().slice(0, 10);
  const { data: zReport, isLoading: zReportLoading } = useQuery({
    queryKey: ['daily-summary', today, selectedLocationId],
    queryFn: () => {
      const params: Record<string, string> = { date: today };
      if (selectedLocationId) params.locationId = selectedLocationId;
      return api.get('/sales/daily-summary', { params }).then((r) => r.data);
    },
    enabled: zReportOpen,
    staleTime: 0,
  });

  // POS-I192: the "Close my shift" link shows only when the signed-in user has an open shift.
  const { data: currentShift } = useQuery({
    queryKey: ['shift-current'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
    enabled: zReportOpen && hasPermission('shifts:read'),
    staleTime: 0,
  });

  const sub = dashboard?.subscription;
  const usage = dashboard?.usage;
  const sales = dashboard?.sales;
  const data = dashboard;
  const trend: DashboardTrendPoint[] = sales?.trend ?? [];
  const lowStockCount: number = usage?.lowStock ?? 0;
  const topProducts: DashboardTopProduct[] = data?.insights?.topProducts ?? [];
  const expenseCategories: DashboardExpenseCategory[] = data?.insights?.expenseSummary?.categories ?? [];
  const fbrFailedToday: number = data?.insights?.fbrToday?.failed ?? 0;
  const pendingFbr: number = (fbrStats?.pending ?? 0) + (fbrStats?.retrying ?? 0);
  const failedFbr: number = fbrStats?.failed ?? 0;

  const quickActions = [
    { name: t('dashboardQaOpenPos'), href: '/pos', icon: ShoppingCart, color: 'from-info to-info/80', show: true },
    { name: t('dashboardQaProducts'), href: '/products', icon: Package, color: 'from-success to-success/80', show: true },
    { name: t('dashboardQaInventory'), href: '/inventory', icon: Boxes, color: 'from-warning to-warning/80', show: canViewInventory },
    { name: t('dashboardQaAccounting'), href: '/accounting', icon: Calculator, color: 'from-primary to-primary/80', show: canViewAccounting },
    { name: t('dashboardQaFbr'), href: '/fbr', icon: Receipt, color: 'from-destructive to-destructive/80', show: canViewFbr },
    { name: t('dashboardQaExpenses'), href: '/expenses', icon: Wallet, color: 'from-success to-success/80', show: canViewExpenses },
    { name: t('dashboardQaLocations'), href: '/locations', icon: MapPin, color: 'from-warning to-warning/80', show: canViewLocations },
    { name: t('dashboardQaTransfers'), href: '/stock-transfers', icon: ArrowRightLeft, color: 'from-primary to-primary/80', show: canViewTransfers },
    { name: t('dashboardQaReports'), href: '/reports', icon: TrendingUp, color: 'from-info to-info/80', show: canViewReports },
    { name: t('dashboardQaPromotions'), href: '/promotions', icon: BadgePercent, color: 'from-primary to-primary/80', show: canViewPromotions },
  ].filter((a) => a.show);

  const maxBillLabel =
    sub && sub.estimatedBill !== undefined ? t('dashboardPerMonth', { amount: formatCurrency(sub.estimatedBill) }) : null;

  const showOnboarding = !onboardingDismissed && usage?.products?.used === 0;

  // At least one insight capability — otherwise the Insights tab shows an EmptyState (T-475).
  const canViewAnyInsight =
    canViewInventory || canViewExpenses || canViewPurchases || canViewBank || canViewRevenue || canViewFbr;

  return (
    <div className="space-y-6 page-enter">
      {/* Z-Report Modal */}
      {zReportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50 p-4 print:bg-transparent">
          <div className="w-full max-w-md rounded-xl bg-background border shadow-xl print:shadow-none">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="font-bold text-lg">{t('dashboardEodReport')}</h2>
                <p className="text-xs text-muted-foreground">{t('dashboardEodLive')}</p>
                <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <button onClick={() => setZReportOpen(false)} className="print:hidden rounded-md p-1.5 hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {zReportLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : zReport && (zReport.count ?? 0) === 0 && (zReport.voids?.count ?? 0) === 0 && (zReport.returns?.count ?? 0) === 0 ? (
                <EmptyState title={t('dashboardEodEmpty')} subtitle={t('dashboardEodLive')} />
              ) : zReport ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-success/10 border border-success/30 p-3 text-center">
                      <p className="text-xs text-success-text font-medium">{t('dashboardTotalSales')}</p>
                      <p className="text-2xl font-bold text-success-text">{zReport.count ?? 0}</p>
                      <p className="text-xs text-success-text">{t('dashboardCompletedSales')}</p>
                    </div>
                    <div className="rounded-lg bg-info/10 border border-info/30 p-3 text-center">
                      <p className="text-xs text-info-text font-medium">{t('dashboardTotalRevenue')}</p>
                      <p className="text-xl font-bold text-info-text">{formatCurrency(zReport.totalAmount ?? 0)}</p>
                      <p className="text-xs text-info-text">{t('dashboardGross')}</p>
                    </div>
                  </div>
                  {/* POS-I192: tax, discounts, returns, voids, tips and the day's shifts */}
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('dashboardTaxCollected')}</span><span className="font-semibold">{formatCurrency(Number(zReport.totalTax ?? 0))}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('dashboardDiscounts')}</span><span className="font-semibold">{formatCurrency(Number(zReport.totalDiscount ?? 0))}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('dashboardReturns')} ({zReport.returns?.count ?? 0})</span><span className="font-semibold">{formatCurrency(Number(zReport.returns?.amount ?? 0))}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('dashboardVoids')} ({zReport.voids?.count ?? 0})</span><span className="font-semibold">{formatCurrency(Number(zReport.voids?.amount ?? 0))}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('dashboardTips')}</span><span className="font-semibold">{formatCurrency(Number(zReport.totalTips ?? 0))}</span></div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t('dashboardShiftsToday', { closed: zReport.shifts?.closed ?? 0, open: zReport.shifts?.open ?? 0 })}</span>
                      <span className="font-semibold">{t('dashboardShiftVariance')}: {formatCurrency(Number(zReport.shifts?.varianceTotal ?? 0))}</span>
                    </div>
                    {currentShift?.id && (
                      <Link to="/sell/shifts" onClick={() => setZReportOpen(false)} className="print:hidden inline-block text-sm font-medium text-primary-text underline">
                        {t('dashboardCloseMyShift')}
                      </Link>
                    )}
                  </div>
                  {/* Payment method breakdown */}
                  {zReport.byPaymentMethod && Object.keys(zReport.byPaymentMethod).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t('dashboardByPaymentMethod')}</p>
                      <div className="space-y-1.5">
                        {Object.entries(zReport.byPaymentMethod).map(([method, amount]: [string, unknown]) => (
                          <div key={method} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                              {method === 'CASH' ? <Banknote className="h-3.5 w-3.5" /> : method === 'BANK_TRANSFER' ? <Building2 className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                              {t(`dashboardPaymentMethod_${method}`, { defaultValue: method.replace(/_/g, ' ') })}
                            </span>
                            <span className="font-semibold">{formatCurrency(Number(amount))}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {canViewFbr && (
                    <div className="rounded-lg bg-muted/40 p-3">
                      <p className="text-xs font-semibold text-muted-foreground mb-1.5">{t('dashboardFbrStatus')}</p>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {/* That day's submissions (from the Z-report), not an all-time total (T-257) */}
                        <span className="text-success-text">✓ {t('dashboardAccepted')}: {zReport.fbr?.accepted ?? 0}</span>
                        <span className="text-warning-text">⏳ {t('dashboardPending')}: {zReport.fbr?.pending ?? 0}</span>
                        <span className="text-destructive-text">✗ {t('dashboardFailed')}: {zReport.fbr?.failed ?? 0}</span>
                        {(zReport.fbr?.offline ?? 0) > 0 && (
                          <span className="text-muted-foreground">◦ {t('dashboardOffline')}: {zReport.fbr.offline}</span>
                        )}
                      </div>
                      {/* POS-I353: sales voided or fully returned leave Total Sales but keep their FBR invoice */}
                      {(zReport.fbr?.offline ?? 0) > 0 && ((zReport.voids?.count ?? 0) + (zReport.refundedSales?.count ?? 0)) > 0 && (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          {t('dashboardFbrExtraNote', { count: (zReport.voids?.count ?? 0) + (zReport.refundedSales?.count ?? 0) })}
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-8">{t('dashboardNoSalesToday')}</p>
              )}
            </div>
            {/* POS-I240: the Z-report slip carries the product line */}
            <PoweredBy className="pb-3" />
            <div className="flex gap-2 border-t px-6 py-4 print:hidden">
              <Button variant="outline" onClick={() => window.print()} className="flex-1 gap-2">
                <Printer className="h-4 w-4" /> {t('dashboardPrintReport')}
              </Button>
              <Button onClick={() => setZReportOpen(false)} className="flex-1">{t('close')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary via-primary to-primary animate-gradient p-6 text-primary-foreground shadow-lg">
        <div className="absolute -end-12 -top-12 h-48 w-48 rounded-full bg-primary-foreground/10 blur-2xl" />
        <div className="absolute -bottom-16 -start-16 h-56 w-56 rounded-full bg-primary-foreground/5 blur-3xl" />
        <div className="absolute inset-0 bg-mosaic opacity-10" />
        <div className="relative animate-fade-in-up flex items-start justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold break-words">{t('dashboardWelcomeBack', { name: user?.firstName })}</h1>
            {/* POS-I182: a very long business name stays inside the banner (two lines at most) */}
            <p className="mt-1 text-primary-foreground/80 break-words line-clamp-2">
              <Trans
                i18nKey="dashboardOverviewIntro"
                ns="common"
                values={{ tenant: tenant?.name }}
                components={{ b: <span className="font-semibold" /> }}
              />
              {canViewLocations && selectedLocationId && (
                <span className="ms-1">· <span className="font-semibold">{selectedLocationName}</span></span>
              )}
              {canViewLocations && !selectedLocationId && (
                <span className="ms-1">· <span className="font-semibold">{t('dashboardAllLocations')}</span></span>
              )}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <Link to="/pos">
                <Button className="bg-background text-primary-text hover:bg-info/10 font-semibold shadow-sm">
                  <ShoppingCart className="me-2 h-4 w-4" />
                  {t('dashboardOpenPosTerminal')}
                  <ArrowRight className="ms-2 h-4 w-4" />
                </Button>
              </Link>
              <Button
                variant="secondary"
                onClick={() => setZReportOpen(true)}
                className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 border-primary-foreground/30 border font-medium"
              >
                <Printer className="me-2 h-4 w-4" />
                {t('dashboardZReport')}
              </Button>
            </div>
          </div>
          {sub && (
            <div className="hidden sm:flex flex-col items-end gap-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium bg-primary-foreground/20 text-primary-foreground`}>
                {planLabel(sub.planName)}
              </span>
              {maxBillLabel && <span className="text-xs text-primary-foreground/70"><bdi dir="ltr">{maxBillLabel}</bdi></span>}
            </div>
          )}
        </div>
      </div>

      {/* Test mode warning */}
      {dashboard?.isTest && (
        <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-warning-text shrink-0" />
          <div>
            <p className="text-sm font-medium text-warning-text">{t('dashboardTestModeActive')}</p>
            <p className="text-xs text-warning-text">
              {t('dashboardTestModeDesc')}
            </p>
          </div>
          <Link to="/settings" className="ms-auto">
            <Button variant="outline" size="sm" className="text-warning-text border-warning/30 hover:bg-warning/10 text-xs">{t('dashboardSettings')}</Button>
          </Link>
        </div>
      )}

      {/* Onboarding wizard for new tenants */}
      {showOnboarding && (
        <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-primary/10">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">🎉 {t('dashboardOnboardingTitle')}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{t('dashboardOnboardingSubtitle')}</p>
              </div>
              <button onClick={() => { setOnboardingDismissed(true); localStorage.setItem('onboarding-dismissed', '1'); }} className="text-muted-foreground hover:text-foreground rounded-md p-1 hover:bg-muted/50">
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Step pills */}
            <div className="flex gap-1.5 mb-4">
              {ONBOARDING_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setOnboardingStep(i)}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${i <= onboardingStep ? 'bg-primary' : 'bg-muted'}`}
                />
              ))}
            </div>
            {/* Current step */}
            <div className="flex items-center gap-4">
              <span className="text-3xl">{ONBOARDING_STEPS[onboardingStep].icon}</span>
              <div className="flex-1">
                <p className="text-sm font-semibold">{t(ONBOARDING_STEPS[onboardingStep].title)}</p>
                <p className="text-xs text-muted-foreground">{t(ONBOARDING_STEPS[onboardingStep].desc)}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                {onboardingStep > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setOnboardingStep((s) => s - 1)} className="text-xs h-8">← {t('dashboardBack')}</Button>
                )}
                <Link to={ONBOARDING_STEPS[onboardingStep].href}>
                  <Button size="sm" className="text-xs h-8">{t(ONBOARDING_STEPS[onboardingStep].cta)}</Button>
                </Link>
                {onboardingStep < ONBOARDING_STEPS.length - 1 && (
                  <Button size="sm" variant="outline" onClick={() => setOnboardingStep((s) => s + 1)} className="text-xs h-8">{t('dashboardSkip')} →</Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <TabBar
        tabs={[
          { key: 'overview', label: t('dashboardTabOverview') },
          { key: 'insights', label: t('dashboardTabInsights') },
          { key: 'plan', label: t('dashboardTabPlanBilling') },
        ]}
        activeTab={tab}
        onTabChange={(k) => setTab(k as 'overview' | 'insights' | 'plan')}
        bordered={false}
      />

      {tab === 'overview' && (<>
      {/* Top KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          {
            title: t('dashboardKpiRevenue30d'),
            value: sales?.last30DaysAmount ?? 0,
            format: 'currency' as const,
            sub: t('dashboardSalesCount', { count: sales?.last30Days ?? 0 }),
            icon: DollarSign,
            accent: 'success' as const,
            href: '/reports',
            show: canViewRevenue && sales?.last30DaysAmount != null,
          },
          {
            title: t('dashboardKpiProducts'),
            value: usage?.products.used ?? 0,
            format: 'number' as const,
            sub: usage?.products.max ? t('dashboardProductsLimit', { max: usage.products.max.toLocaleString() }) : t('dashboardUnlimited'),
            icon: Package,
            accent: 'info' as const,
            href: '/products',
            show: true,
          },
          {
            title: t('dashboardKpiCustomers'),
            value: usage?.customers ?? 0,
            format: 'number' as const,
            sub: t('dashboardRegistered'),
            icon: Users,
            accent: 'default' as const,
            href: '/customers',
            show: canViewCustomers,
          },
          {
            title: t('dashboardKpiLocations'),
            value: typeof usage?.locations === 'object' ? usage.locations.used : (usage?.locations ?? 0),
            format: 'number' as const,
            sub: t('dashboardActiveStoresWarehouses'),
            icon: MapPin,
            accent: 'warning' as const,
            href: '/locations',
            show: canViewLocations,
          },
        ].filter((s) => s.show).map((stat, index) => (
          <Link key={stat.title} to={stat.href} className="block">
            <StatCard
              label={stat.title}
              value={stat.format === 'currency' ? formatMoney2(Number(stat.value)) : stat.value}
              format={stat.format === 'currency' ? 'raw' : stat.format}
              helper={stat.sub}
              icon={stat.icon}
              accent={stat.accent}
              className={`animate-fade-in-up stagger-${index + 1} h-full cursor-pointer transition-all hover:-translate-y-1 hover:shadow-md`}
            />
          </Link>
        ))}
      </div>
      </>)}

      {tab === 'insights' && !canViewAnyInsight && (
        <EmptyState
          icon={PieChart}
          title={t('dashboardNoInsightsTitle')}
          description={t('dashboardNoInsightsDesc')}
        />
      )}

      {tab === 'insights' && canViewAnyInsight && (<>
      {/* Insights KPI cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {(canViewInventory) && data?.insights?.inventoryValue !== null && (
          <DataCard
            title={t('dashboardInsightInventoryValue')}
            value={data?.insights?.inventoryValue == null ? null : formatMoney2(data.insights.inventoryValue)}
            format="text"
            subLabel={t('dashboardInsightInventoryHelper')}
            icon={Package}
            isLoading={isLoading}
          />
        )}

        {canViewExpenses && data?.insights?.expenseSummary !== null && (
          <DataCard
            title={t('dashboardInsightExpenses30d')}
            value={data?.insights?.expenseSummary?.total == null ? null : formatMoney2(data.insights.expenseSummary.total)}
            format="text"
            subLabel={t('dashboardCategoryCount', { count: data?.insights?.expenseSummary?.categoryCount ?? 0 })}
            icon={Receipt}
            isLoading={isLoading}
          />
        )}

        {canViewPurchases && data?.insights?.openPurchaseOrders !== null && (
          <DataCard
            title={t('dashboardInsightOpenPos')}
            value={data?.insights?.openPurchaseOrders?.count ?? null}
            format="number"
            subLabel={
              data?.insights?.openPurchaseOrders?.totalValue
                ? t('dashboardPoTotal', { amount: formatMoney2(data.insights.openPurchaseOrders.totalValue) })
                : t('dashboardNoPendingOrders')
            }
            icon={ShoppingBag}
            isLoading={isLoading}
          />
        )}

        {canViewBank && data?.insights?.bankBalance !== null && (
          <DataCard
            title={t('dashboardInsightBankBalance')}
            value={data?.insights?.bankBalance == null ? null : formatMoney2(data.insights.bankBalance)}
            format="text"
            subLabel={t('dashboardAcrossAllAccounts')}
            icon={Landmark}
            isLoading={isLoading}
          />
        )}

        {canViewRevenue && data?.sales?.avgOrderValue != null && (
          <DataCard
            title={t('dashboardInsightAvgOrder')}
            value={data?.sales?.avgOrderValue == null ? null : formatMoney2(data.sales.avgOrderValue)}
            format="text"
            subLabel={t('dashboardLast30Days')}
            icon={TrendingUp}
            isLoading={isLoading}
          />
        )}

        {canViewRevenue && data?.insights?.grossMargin !== null && data?.insights?.grossMargin !== undefined && (
          <DataCard
            title={t('dashboardInsightGrossMargin')}
            value={data?.insights?.grossMargin ?? null}
            format="percent"
            subLabel={t('dashboardRevenueMinusCogs')}
            icon={PieChart}
            isLoading={isLoading}
          />
        )}

        {canViewFbr && data?.insights?.fbrToday !== null && (
          <DataCard
            title={t('dashboardInsightFbrToday')}
            value={data?.insights?.fbrToday?.submitted ?? 0}
            format="number"
            subLabel={
              data?.insights?.fbrToday?.failed
                ? t('dashboardFbrFailedCount', { count: data.insights.fbrToday.failed })
                : t('dashboardAllSubmittedOk')
            }
            icon={Receipt}
            isLoading={isLoading}
          />
        )}
      </div>
      </>)}

      {tab === 'overview' && canViewRevenue && (<>
      {/* Charts row — trend/daily-sales need reports:read (T-468) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 7-day revenue area chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">{t('dashboardRevenueTrend')}</CardTitle>
                <CardDescription>{t('dashboardLast7Days')}</CardDescription>
              </div>
              {canViewReports && (
                <Link to="/reports">
                  <Button variant="ghost" size="sm" className="text-xs">{t('dashboardViewReports')} <ArrowRight className="ms-1 h-3 w-3" /></Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t('loading')}</div>
            ) : trend.length === 0 || trend.every((d) => d.revenue === 0) ? (
              <div className="h-48 flex flex-col items-center justify-center gap-2">
                <TrendingUp className="h-10 w-10 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t('dashboardNoSalesData')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto min-w-0">
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={series[0]} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={series[0]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: axis }} stroke={axis} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: axis }} stroke={axis} axisLine={false} tickLine={false} tickFormatter={(v) => `₨${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => [formatMoney2(Number(v)), t('dashboardRevenue')]}
                    labelStyle={{ fontWeight: 600 }}
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke={series[0]} strokeWidth={2} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily sales count bar chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('dashboardDailySales')}</CardTitle>
            <CardDescription>{t('dashboardTransactionsPerDay')}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t('loading')}</div>
            ) : (
              <div className="overflow-x-auto min-w-0">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: axis }} stroke={axis} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: axis }} stroke={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    formatter={(v) => [Number(v), t('dashboardSales')]}
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                  />
                  <Bar dataKey="sales" fill={series[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>)}

      {/* Insights widgets row */}
      {tab === 'insights' && (
        (canViewRevenue && topProducts.length > 0) ||
        (canViewExpenses && expenseCategories.length > 0) ||
        (canViewFbr && fbrFailedToday > 0)
      ) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Top Products widget */}
          {canViewRevenue && topProducts.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('dashboardTopProducts30d')}</h3>
              <div className="space-y-2">
                {topProducts.map((p: DashboardTopProduct, i: number) => (
                  <div key={p.productId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                      <span className="truncate text-foreground">{p.productName}</span>
                    </div>
                    <span className="text-xs font-medium text-foreground ms-2 shrink-0">
                      {formatMoney2(p.revenue)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expense Breakdown widget */}
          {canViewExpenses && expenseCategories.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('dashboardTopExpenses30d')}</h3>
              <div className="space-y-2">
                {expenseCategories.map((cat: DashboardExpenseCategory) => (
                  <div key={cat.name} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate">{cat.name ?? t('dashboardUncategorized')}</span>
                    <span className="text-xs font-medium text-foreground ms-2 shrink-0">
                      {formatMoney2(cat.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent FBR failures strip */}
          {canViewFbr && fbrFailedToday > 0 && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-destructive-text shrink-0" />
              <div>
                <p className="text-sm font-medium text-destructive-text">
                  {t('dashboardFbrFailedToday', { count: fbrFailedToday })}
                </p>
                <p className="text-xs text-destructive-text">
                  {t('dashboardFbrCheckRetry')}
                </p>
              </div>
              <a href="/finance/fbr" className="ms-auto text-xs font-medium text-destructive-text underline underline-offset-2 shrink-0">
                {t('dashboardViewFbr')}
              </a>
            </div>
          )}
        </div>
      )}

      {tab === 'plan' && (<>
      {/* Subscription + Usage */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Subscription card */}
        {sub ? (
          <Card className={`border-2 ${sub.status === 'SUSPENDED' ? 'border-destructive/30 bg-gradient-to-br from-destructive/10 to-transparent' : 'border-primary/20 bg-gradient-to-br from-primary/5 to-transparent'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">{t('dashboardYourPlan')}</CardTitle>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[sub.status] ?? 'bg-muted text-muted-foreground'}`}>
                  {t(`dashboardStatus_${sub.status}`, { defaultValue: sub.status })}
                </span>
              </div>
              <div className="flex items-end gap-1 mt-1">
                <span className="text-2xl font-bold">{planLabel(sub.planName)}</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {sub.status === 'SUSPENDED' && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-3 py-2">
                  <p className="text-xs font-semibold text-destructive-text">
                    ⚠️ {t('dashboardSuspendedTitle')}
                  </p>
                  <p className="text-xs text-destructive-text mt-0.5">
                    {t('dashboardSuspendedDesc')}
                  </p>
                </div>
              )}
              {/* Billing formula breakdown — billing:read only (T-468) */}
              {canViewBilling && sub.priceMonthly !== undefined && sub.estimatedBill !== undefined && (
              <div className="pt-2 border-t space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{t('dashboardBillBreakdown')}</p>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{t('dashboardPlanBase', { plan: planLabel(sub.planName) })}</span>
                  <span><bdi dir="ltr">{formatCurrency(sub.priceMonthly)}</bdi></span>
                </div>
                {(sub.addons ?? []).map((a: DashboardAddon) => {
                  // POS-I189: x qty and "each" only for per-unit add-ons; the amount on the right is the line total
                  const perUnit = a.type === 'QUANTITY' && a.quantity > 1;
                  const unit = a.unitPrice ?? a.price ?? 0;
                  return (
                    <div key={a.name} className="flex justify-between gap-3 text-xs">
                      <span className="text-muted-foreground min-w-0">
                        <span className="flex items-center gap-1">
                          <CreditCard className="h-3 w-3 shrink-0 text-primary-text" />
                          <span className="break-words">{addonLabel(a.key, a.name)}</span>
                          {perUnit && <span className="text-primary-text"> ×{a.quantity}</span>}
                        </span>
                        {perUnit && (
                          <span className="block text-[11px] text-muted-foreground/80"><bdi dir="ltr">{t('dashboardEachPrice', { price: formatCurrency(unit) })}</bdi></span>
                        )}
                      </span>
                      <span className="shrink-0"><bdi dir="ltr">{formatCurrency(a.lineTotal ?? a.price ?? 0)}</bdi></span>
                    </div>
                  );
                })}
                <div className="flex justify-between text-xs font-bold border-t pt-1 mt-1">
                  <span>{t('dashboardTotalPerMonth')}</span>
                  <span className="text-primary-text"><bdi dir="ltr">{formatCurrency(sub.estimatedBill)}</bdi></span>
                </div>
                <a
                  href={WHATSAPP_URL(t('dashboardWaMsgAddFeatures'))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-1.5 rounded-md bg-success py-1.5 text-xs font-semibold text-success-foreground hover:bg-success/90 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t('dashboardAddFeaturesWhatsapp')}
                </a>
              </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="flex flex-col items-center justify-center py-10 gap-2 text-center">
            <CreditCard className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">{t('dashboardNoSubscription')}</p>
            <p className="text-xs text-muted-foreground">{t('dashboardContactPlatformAdmin')}</p>
          </Card>
        )}

        {/* Usage card */}
        {usage && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-info-text" />
                {t('dashboardPlanUsage')}
              </CardTitle>
              <CardDescription>{t('dashboardPlanUsageDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <UsageGauge
                used={usage.users.used}
                max={usage.users.max}
                label={t('dashboardUsers')}
                trialPlanHref={sub?.status === 'TRIAL' ? (hasPermission('settings:read') ? '/settings?tab=team#my-plan' : null) : undefined}
              />
              <UsageGauge used={usage.products.used} max={usage.products.max} label={t('dashboardKpiProducts')} />
              {canViewLocations && usage.locations && (
                <UsageGauge used={usage.locations.used} max={usage.locations.max ?? null} label={t('dashboardKpiLocations')} />
              )}
              {canViewPromotions && usage.promotions && (
                <UsageGauge used={usage.promotions.used} max={usage.promotions.max ?? null} label={t('dashboardPromotions')} />
              )}
              <div className="flex justify-between text-xs border-t pt-1">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" /> {t('dashboardKpiCustomers')}
                </span>
                <span className="font-medium"><bdi dir="ltr">{(usage.customers ?? 0).toLocaleString()}</bdi></span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </>)}

      {/* Alerts + quick actions */}
      {tab === 'overview' && (
      <div className="space-y-4">
          {/* Low stock alert */}
          {canViewInventory && lowStockCount > 0 && (
            <Card className="border-warning/30 bg-warning/10">
              <CardContent className="flex items-center gap-3 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning/10">
                  <AlertTriangle className="h-5 w-5 text-warning-text" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning-text">
                    {t('dashboardLowStockItems', { count: lowStockCount })}
                  </p>
                  <p className="text-xs text-warning-text">{t('dashboardLowStockDesc')}</p>
                </div>
                <Link to="/inventory/stock">
                  <Button size="sm" variant="outline" className="border-warning/30 text-warning-text hover:bg-warning/10 text-xs">{t('view')}</Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* FBR failed alert */}
          {canViewFbr && failedFbr > 0 && (
            <Card className="border-destructive/30 bg-destructive/10">
              <CardContent className="flex items-center gap-3 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
                  <Receipt className="h-5 w-5 text-destructive-text" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive-text">
                    {t('dashboardFbrSubmissionsFailed', { count: failedFbr })}
                  </p>
                  <p className="text-xs text-destructive-text">{t('dashboardFbrFailedDesc')}</p>
                </div>
                <Link to="/finance/fbr">
                  <Button size="sm" variant="outline" className="border-destructive/30 text-destructive-text hover:bg-destructive/10 text-xs">{t('dashboardFixNow')}</Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* FBR pending alert */}
          {canViewFbr && pendingFbr > 0 && failedFbr === 0 && (
            <Card className="border-info/30 bg-info/10">
              <CardContent className="flex items-center gap-3 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info/10">
                  <Receipt className="h-5 w-5 text-info-text" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-info-text">
                    {t('dashboardFbrSubmissionsPending', { count: pendingFbr })}
                  </p>
                  <p className="text-xs text-info-text">{t('dashboardFbrPendingDesc')}</p>
                </div>
                <Link to="/finance/fbr">
                  <Button size="sm" variant="outline" className="border-info/30 text-info-text hover:bg-info/10 text-xs">{t('view')}</Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Quick actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('dashboardQuickActions')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {quickActions.slice(0, 9).map((action) => (
                  <Link key={action.name} to={action.href}>
                    <div className="flex flex-col items-center gap-1.5 rounded-xl p-2.5 text-center hover:scale-105 transition-transform duration-200">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${action.color} text-primary-foreground shadow-sm`}>
                        <action.icon className="h-4 w-4" />
                      </div>
                      <span className="text-[10px] font-medium leading-tight">{action.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'plan' && (<>
      {/* Features & Add-ons Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4 text-primary" />
                {t('dashboardFeaturesAddons')}
              </CardTitle>
              <CardDescription>{t('dashboardFeaturesDesc')}</CardDescription>
            </div>
            <a
              href={WHATSAPP_URL(t('dashboardWaMsgUpgrade'))}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-md bg-success px-3 py-1.5 text-xs font-semibold text-success-foreground hover:bg-success/90 transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t('dashboardUpgradeWhatsapp')}
            </a>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {ALL_FEATURES.map((f) => {
              const enabled = hasPermission(f.perm) || hasPermission('*');
              return (
                <div
                  key={f.perm}
                  className={`flex items-start gap-3 rounded-lg border p-3 ${
                    enabled
                      ? 'border-success/30 bg-success/10'
                      : 'border-border bg-muted/20'
                  }`}
                >
                  <div className={`mt-0.5 shrink-0 rounded-full p-1 ${enabled ? 'bg-success/10' : 'bg-muted'}`}>
                    {enabled ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-success-text" />
                    ) : (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold leading-tight ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{t(f.label)}</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-0.5 leading-tight">{t(f.desc)}</p>
                    {!enabled && f.price && (
                      <a
                        href={WHATSAPP_URL(
                          // Prices only for billing:read holders (T-473, consistent with T-468).
                          canViewBilling
                            ? t('dashboardWaMsgAddFeaturePrice', { feature: t(f.label), price: formatCurrency(f.price) })
                            : t('dashboardWaMsgAddFeature', { feature: t(f.label) }),
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-success-text hover:underline"
                      >
                        {canViewBilling
                          ? t('dashboardAddViaWhatsappPrice', { price: formatCurrency(f.price) })
                          : t('dashboardAddViaWhatsapp')}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </>)}

      {tab === 'overview' && (<>
      {/* Recent sales */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{t('dashboardRecentSales')}</CardTitle>
              <CardDescription>{t('dashboardLatestTransactions')}</CardDescription>
            </div>
            {canViewReports && (
              <Link to="/reports">
                <Button variant="ghost" size="sm" className="text-xs">
                  {t('dashboardViewAll')} <ArrowRight className="ms-1 h-3 w-3" />
                </Button>
              </Link>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!sales?.recent?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingCart className="mb-3 h-12 w-12 text-muted-foreground/30 animate-pulse-soft" />
              <p className="text-sm font-medium text-muted-foreground">{t('dashboardEodEmpty')}</p>
              <p className="mt-1 text-xs text-muted-foreground/70">{t('dashboardHeadToPos')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sales.recent.map((sale: DashboardRecentSale) => (
                <div
                  key={sale.id}
                  className="flex items-center justify-between rounded-lg border border-s-2 border-s-indigo-500/50 p-3 transition-colors hover:bg-accent/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-info/10 text-info-text">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <RecordLink type="invoice" id={sale.id} label={sale.invoiceNumber} className="text-sm font-medium" />
                        {sale.status && <StatusBadge status={sale.status} className="text-[10px] px-1.5 py-0" />}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {formatRelativeDate(sale.createdAt)} • {t('dashboardItemCount', { count: sale.itemCount })}
                        {sale.customerName && ` • ${sale.customerName}`}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-bold">{formatMoney2(sale.totalAmount)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restaurant stats widget */}
      {canViewRestaurant && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('dashboardActiveOrders')}</span>
            </div>
            <div className="text-2xl font-bold">{restaurantStats?.activeOrders ?? '—'}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('dashboardTablesAvailable')}</span>
            </div>
            <div className="text-2xl font-bold">
              {restaurantStats !== undefined
                ? `${restaurantStats.tablesAvailable}/${restaurantStats.tablesTotal}`
                : '—'}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('dashboardTodaysReservations')}</span>
            </div>
            <div className="text-2xl font-bold">{restaurantStats?.todayReservations ?? '—'}</div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

